"use client"

import { forwardRef, useImperativeHandle, useRef } from "react"
import {
    useDateSegment,
    useTimeField,
    type AriaTimeFieldProps,
    type TimeValue,
} from "@react-aria/datepicker"
import {
    useTimeFieldState,
    type DateFieldState,
    type DateSegment,
} from "@react-stately/datepicker"

import { cx, focusInput } from "@/lib/utils"


type TimeSegmentProps = {
    segment: DateSegment
    state: DateFieldState
}

const TimeSegment = ({ segment, state }: TimeSegmentProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const { segmentProps } = useDateSegment(segment, state, ref)

    // Skip rendering for any non-editable segments except colon
    if (
        !segment.isEditable &&
        segment.type === "literal" &&
        segment.text !== ":"
    ) {
        return null
    }

    return (
        <div
            {...segmentProps}
            ref={ref}
            className={cx(
                // base
                "relative block w-full appearance-none rounded-md border px-2.5 py-1.5 text-left uppercase tabular-nums shadow-xs outline-hidden transition sm:text-sm",
                // border color
                "border-gray-300",
                // text color
                "text-gray-900",
                // background color
                "bg-white",
                // focus
                focusInput,
                // invalid (optional)
                "group-aria-invalid/time-input:border-red-500 group-aria-invalid/time-input:ring-2 group-aria-invalid/time-input:ring-red-200 invalid:border-red-500 invalid:ring-2 invalid:ring-red-200 ",
                {
                    "w-fit! border-none bg-transparent px-0 text-gray-400 shadow-none":
                        segment.type === "literal",
                    "border-gray-300 bg-gray-100 text-gray-400":
                        state.isDisabled && segment.text !== ":",
                },
            )}
        >
            {segment.isPlaceholder ? segment.placeholder : segment.text}
        </div>
    )
}

type TimeInputProps = Omit<
    AriaTimeFieldProps<TimeValue>,
    "label" | "shouldForceLeadingZeros" | "description" | "errorMessage"
>

const TimeInput = forwardRef<HTMLDivElement, TimeInputProps>(
    ({ hourCycle, ...props }: TimeInputProps, ref) => {
        const innerRef = useRef<HTMLDivElement>(null)

        useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
            ref,
            () => innerRef?.current,
        )

        const locale = window !== undefined ? window.navigator.language : "en-US"

        const state = useTimeFieldState({
            hourCycle: hourCycle,
            locale: locale,
            shouldForceLeadingZeros: true,
            autoFocus: true,
            ...props,
        })

        const { fieldProps } = useTimeField(
            {
                ...props,
                hourCycle: hourCycle,
                shouldForceLeadingZeros: true,
            },
            state,
            innerRef,
        )

        return (
            <div
                {...fieldProps}
                ref={innerRef}
                className="group/time-input inline-flex w-full gap-x-2"
            >
                {state.segments.map((segment, i) => (
                    <TimeSegment key={i} segment={segment} state={state} />
                ))}
            </div>
        )
    },
)
TimeInput.displayName = "TimeInput"

export { TimeInput }